import {
  listApprovals,
  listApprovalsForContributor,
  getUserMatchingApprover,
  createApprovals,
  getActiveUser,
  updateApproval,
  deleteApproval,
  getCampaignApprovalInformation,
  updateApprovals,
  approveWholeCampaign,
  rejectWholeCampaign,
} from "services/apiMiddleware";

const approvals = {
  state: {
    isLoading: false,
    error: null,
    items: [],
  },
  reducers: {
    loadStart: (state) => {
      return {
        ...state,
        isLoading: true,
      };
    },
    loadSuccess: (state, items) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items,
      };
    },
    createSuccess: (state, newItem) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: [...state.items, newItem],
      };
    },
    updateMultipleSuccess: (state, updatedItems, campaignId) => {
      const stateCopy = { ...state };
      const campaign = stateCopy.items.find((c) => c.campaignID === campaignId);
      const approvals = campaign.approvals;

      // Update approvals for this campaign
      for (let i = 0; i < approvals.length; i++) {
        const approval = approvals[i];
        for (let j = 0; j < updatedItems.length; j++) {
          const updatedApproval = updatedItems[j];
          if (approval.id === updatedApproval.id) {
            approvals[i] = updatedApproval;
          }
        }
      }

      // Check new statuses
      const getApprovalStates = (approvalArray) => {
        let approvalStatus = {};
        let pendingApprovals = 0;
        let rejected = false;

        for (let i = 0; i < approvalArray.length; i++) {
          const assetID = approvalArray[i].assetID;
          const status = approvalArray[i].status;
          if (!Object.prototype.hasOwnProperty.call(approvalStatus, assetID)) {
            approvalStatus[assetID] = {
              APPROVED: 0,
              REJECTED: 0,
              PENDING: 0,
            };
          }
          if (status === "APPROVED") {
            approvalStatus[assetID].APPROVED++;
          } else if (status === "REJECTED") {
            approvalStatus[assetID].REJECTED++;
            rejected = true;
          } else {
            approvalStatus[assetID].PENDING++;
            pendingApprovals++;
          }
        }
        return { approvalStatus, pendingApprovals, rejected };
      };
      const { pendingApprovals, rejected } = getApprovalStates(approvals);

      // Update the array with the updated campaign
      campaign.approvals = approvals;
      campaign.isReady = pendingApprovals === 0;
      campaign.pendingAssets = pendingApprovals;
      campaign.rejected = rejected;

      return {
        ...state,
        isLoading: false,
        error: null,
        items: stateCopy.items,
      };
    },
    deleteSuccess: (state, deletedId) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: state.items.filter((item) => item.id !== deletedId),
      };
    },
    failure: (state, error) => {
      return {
        ...state,
        isLoading: false,
        error,
      };
    },
  },
  effects: (dispatch) => ({
    async load() {
      const isSomeAdmin = (userData) => {
        return userData.isAdmin || userData.isSuperAdmin || userData.isPowerAdmin;
      };
      const isContributor = (userData) => {
        return (
          userData.isContributor &&
          !userData.isAdmin &&
          !userData.isSuperAdmin &&
          !userData.isPowerAdmin
        );
      };

      try {
        dispatch.approvals.loadStart();
        const approvalsByCampaign = {};
        const activeUser = await getActiveUser();
        const isAdmin = isSomeAdmin(activeUser.data);
        const isContr = isContributor(activeUser.data);

        // If contributor find him as approver and get his approvals
        let userMatchingApprover;
        if (isContr) {
          const userMatchingApprovers = await getUserMatchingApprover(activeUser.data.username);
          userMatchingApprover = userMatchingApprovers.data[0];
        }
        if (isContr && !userMatchingApprover?.id) {
          dispatch.approvals.loadSuccess([]);
          return;
        }
        const { data: approvalsList, errorMessage } = isAdmin
          ? await listApprovals()
          : await listApprovalsForContributor(userMatchingApprover.id);

        // Arrange approvals under campaign keys
        approvalsList.forEach((approval) => {
          const { campaignID, ...approvalData } = approval;

          if (!approvalsByCampaign[campaignID]) {
            approvalsByCampaign[campaignID] = [];
          }
          approvalsByCampaign[campaignID].push(approvalData);
        });
        const entries = Object.entries(approvalsByCampaign);

        const approvalsByCampaignArray = (
          await Promise.all(
            entries.map(async ([campaignID, approvals]) => {
              let campaign;
              try {
                const result = await getCampaignApprovalInformation(campaignID);
                if (result && result.data) {
                  campaign = result.data;
                }
              } catch (error) {
                console.log("Error getting campaign data for approvals:", error);
              }

              if (!campaign) {
                return null;
              }

              const uniqueAssetIDs = new Set(approvals.map((approval) => approval.assetID));
              const count = uniqueAssetIDs.size;
              const deadline = approvals[0].deadline;

              const getApprovalStates = (approvalArray) => {
                let approvalStatus = {};
                let pendingApprovals = 0;
                let rejected = false;

                for (const approval of approvalArray) {
                  const assetID = approval.assetID;
                  const status = approval.status;
                  if (!Object.prototype.hasOwnProperty.call(approvalStatus, assetID)) {
                    approvalStatus[assetID] = {
                      APPROVED: 0,
                      REJECTED: 0,
                      PENDING: 0,
                    };
                  }
                  if (status === "APPROVED") {
                    approvalStatus[assetID].APPROVED++;
                  } else if (status === "REJECTED") {
                    approvalStatus[assetID].REJECTED++;
                    rejected = true;
                  } else {
                    approvalStatus[assetID].PENDING++;
                    pendingApprovals++;
                  }
                }
                return { approvalStatus, pendingApprovals, rejected };
              };

              const { pendingApprovals, rejected } = getApprovalStates(approvals);

              return {
                campaignID: campaignID,
                name: campaign.name,
                starts: campaign.start_time,
                ends: campaign.end_time,
                deadline: deadline,
                approvals: approvals,
                amountOfAssets: count,
                pendingAssets: pendingApprovals,
                rejected: rejected,
                ...(isAdmin && {
                  orderlineID: campaign.orderlineID,
                }),
                isReady: pendingApprovals === 0,
                status: campaign.status,
              };
            })
          )
        ).filter(Boolean);

        dispatch.approvals.loadSuccess(approvalsByCampaignArray);
        if (errorMessage) console.warn(errorMessage);
      } catch (error) {
        dispatch.approvals.failure("loading approvals failed");
      }
    },
    async create(createProps) {
      try {
        dispatch.approvals.loadStart();
        const response = await createApprovals(
          createProps.campaign,
          createProps.approverAssetMap,
          createProps.deadline,
          createProps.campaignViewerIds
        );
        dispatch.approvals.createSuccess();
        return response;
      } catch (error) {
        dispatch.approvals.failure("creating approval failed");
        throw error;
      }
    },
    async update(updateProps) {
      try {
        dispatch.approvals.loadStart();
        const { data, errorMessage } = await updateApproval(updateProps);
        if (errorMessage) console.warn(errorMessage);
        dispatch.approvals.updateSuccess(data);
        return data;
      } catch (error) {
        dispatch.approvals.failure("updating approval failed");
        throw error;
      }
    },
    async updateMultiple(updateProps) {
      try {
        const { approvalIds, status, reason, campaignId } = updateProps;
        const result = await updateApprovals(approvalIds, status, reason);
        const hasErrorMessage = (array) => {
          for (const obj of array) {
            if (obj.errorMessage !== undefined) {
              return true;
            }
          }
          return false;
        };
        if (hasErrorMessage(result)) {
          dispatch.approvals.failure("updating approval failed");
        }
        dispatch.approvals.updateMultipleSuccess(
          result.map((item) => item.data),
          campaignId
        );
      } catch (error) {
        dispatch.approvals.failure("updating approval failed", error);
        throw error;
      }
    },
    async approveWholeCampaign(campaignId) {
      try {
        dispatch.approvals.loadStart();
        const { errorMessage } = await approveWholeCampaign(campaignId);
        if (errorMessage) console.warn(errorMessage);

        /**
         * Need to wait a bit for the load to get the updated statuses
         * Could also just do an optimistic update of ui so that
         * Another dispatch.approcals.load() is not needed
         */
        setTimeout(() => {
          dispatch.approvals.load();
        }, 3000);
      } catch (error) {
        dispatch.approvals.failure("deleting approval failed");
        throw error;
      }
    },
    async rejectWholeCampaign(campaignId) {
      try {
        dispatch.approvals.loadStart();
        const { errorMessage } = await rejectWholeCampaign(campaignId);
        if (errorMessage) console.warn(errorMessage);

        setTimeout(() => {
          dispatch.approvals.load();
        }, 3000);
      } catch (error) {
        dispatch.approvals.failure("updating approval failed");
        throw error;
      }
    },
    async delete(approval) {
      try {
        dispatch.approvals.loadStart();
        const { data, errorMessage } = await deleteApproval(approval.id);
        if (errorMessage) console.warn(errorMessage);
        dispatch.approvals.deleteSuccess(data.id);
      } catch (error) {
        dispatch.approvals.failure("deleting approval failed");
        throw error;
      }
    },
  }),
};

export default approvals;
